import { HttpClient, HttpHeaders } from '@angular/common/http';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, forkJoin, Observable, of, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';
import { protectedResources } from 'src/app/auth/auth.config';
import {
  AircraftAutopilotDetail,
  AircraftBookings,
  AircraftDetails,
  AircraftEdit,
  AircraftFlightDetail,
  AircraftSoftwareDetail,
  AircraftSummary,
  DroneActivation,
} from '../models/equipment.model';
@Injectable({
  providedIn: 'root',
})
export class EquipmentService {
  public drone?: Partial<AircraftDetails>;
  $step: BehaviorSubject<number>;
  apiEndpoint: string = protectedResources.apiTodoList.endpoint;
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private http: HttpClient,
    private translate: TranslateService,
  ) {
    this.$step = new BehaviorSubject<number>(1);
  }

  getAllDrones(): Observable<AircraftSummary[]> {
    const headers = new HttpHeaders({
      accept: 'application/vnd.web.aircraft.summary.v2+json',
    });
    return this.http.get<AircraftSummary[]>(`${this.apiEndpoint}/aircraft`, {
      headers,
    });
  }

  getDroneDetails(
    modelKey: string,
    unitNumber: string,
    withParts: boolean = false,
  ) {
    const headers = new HttpHeaders({
      accept: 'application/vnd.web.aircraft.detail.v2+json',
    });
    return this.http.get<AircraftDetails>(
      `${this.apiEndpoint}/aircraft/${modelKey}/${unitNumber}?withParts=${withParts}`,
      { headers },
    );
  }

  getBookingsOfAircraft(
    modelKey: string,
    unitNumber: string,
    month?: number,
    year?: number,
  ) {
    const headers = new HttpHeaders({
      accept: 'application/vnd.web.bookings+json',
    });
    return this.http.get<AircraftBookings[]>(
      `${this.apiEndpoint}/aircraft/${modelKey}/${unitNumber}/bookings?month=${month}&year=${year}`,
      { headers },
    );
  }

  getBookings(startDate: Date, endDate: Date, selectedDrone: AircraftDetails) {
    const bookings$ = of(
      this.getMonthAndYearBetweenDates(startDate, endDate),
    ).pipe(
      switchMap((dateRanges) => {
        const dateRanges$ = dateRanges.map((dateRange) =>
          this.getBookingsOfAircraft(
            selectedDrone.model.key.toString(),
            selectedDrone.unitNumber,
            dateRange.month,
            dateRange.year,
          ),
        );
        return forkJoin(dateRanges$);
      }),
      map((bookingsMap) => bookingsMap.flat(1)),
      takeUntilDestroyed(this.destroyRef),
    );
    return bookings$;
  }

  editDrone(modelKey: string, unitNumber: string, droneEdit: AircraftEdit) {
    const headers = new HttpHeaders({
      accept: 'application/vnd.web.aircraft.detail.v2+json',
      'Content-Type': 'application/vnd.web.aircraft.create+json',
    });
    return this.http.put<AircraftDetails>(
      `${this.apiEndpoint}/aircraft/${modelKey}/${unitNumber}`,
      droneEdit,
      { headers },
    );
  }

  activateEquipment(licenseKey: string, companyId: string): Observable<any> {
    return this.http.post<DroneActivation>(
      `${this.apiEndpoint}/companies/${companyId}/activated_aircrafts`,
      {
        licenseKey: licenseKey,
      },
    );
  }

  getStepAsObservable() {
    return this.$step.asObservable();
  }

  setStep(step: number) {
    if (step > this.$step.value) {
      this.$step.next(step);
    }
  }

  getAircraftAutopilotDetail(
    modelKey: string,
    unitNumber: string,
  ): Observable<AircraftAutopilotDetail> {
    return this.http.get<AircraftAutopilotDetail>(
      `${this.apiEndpoint}/aircraft/${modelKey}/${unitNumber}/autopilot`,
    );
  }

  getAircraftSoftwareDetail(
    modelKey: string,
    unitNumber: string,
  ): Observable<AircraftSoftwareDetail> {
    return this.http.get<AircraftSoftwareDetail>(
      `${this.apiEndpoint}/aircraft/${modelKey}/${unitNumber}/software`,
    );
  }

  getMonthAndYearBetweenDates(startDate: Date, endDate: Date) {
    const result = [];
    if (startDate > endDate) {
      throw new Error(
        this.translate.instant('equipment.error.invalid_date_range'),
      );
    }
    let current = new Date(startDate);
    while (current <= endDate) {
      result.push({
        month: current.getMonth() + 1,
        year: current.getFullYear(),
      });
      current.setMonth(current.getMonth() + 1);
      current.setDate(1);
    }
    return result;
  }

  getFlightsOFAircraft(
    modelKey: string,
    unitNumber: string,
  ): Observable<AircraftFlightDetail[]> {
    const headers = new HttpHeaders({
      accept: 'application/vnd.web.flight.details+json',
    });
    return this.http.get<AircraftFlightDetail[]>(
      `${this.apiEndpoint}/aircraft/${modelKey}/${unitNumber}/flight`,
      { headers },
    );
  }
}
